import { Button, openToast } from "@vinomofo/components";
import { useEffect, useState } from "react";

import { COLORS } from "../../styles";
import { Cart } from "../../contexts/cart/types";
import { ErrorToast } from "../toast";
import { VinoUser } from "../../contexts/auth/state";
import styled from "styled-components";
import { trackOneClickCheckout } from "../../utils/event-tracking";
import { useCart } from "../../hooks/use-cart";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";

export const OneClickCheckoutButton = styled(Button)`
  padding: 6px !important;
  line-height: 1 !important;
  min-height: 50px !important;
  & small {
    font-weight: normal;
    font-size: 12px;
    line-height: 12px;
    font-family: "Graphik", sans-serif;
  }
`;

export interface OneClickCheckoutButtonProps {
  buttonColour?: string;
  buttonBorderColor?: string;
  buttonTextColor?: string;
  buttonText?: string;
  user?: VinoUser;
  cartId?: string;
  cart?: Cart;
}

export const OneClickCheckout = ({
  buttonColour = COLORS.oneClickCheckout,
  buttonBorderColor = COLORS.oneClickCheckout,
  buttonTextColor = COLORS.white,
  user,
  cartId,
  buttonText,
  cart,
}: OneClickCheckoutButtonProps): JSX.Element => {
  const { isLoading, isRedirecting: cartRedirect } = useCart();
  const {
    isBuying,
    isProcessing,
    processMessage,
    isRedirecting,
    doOneClickCheckout,
    setRedirecting,
    resetState,
  } = useOneClickCheckout();
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const customerId =
    userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];

  const [address, setAddress] = useState(null);
  const [defaultCard, setDefaultCard] = useState(null);
  let redirectTimeout;

  const handleOneClickCheckoutClick = async () => {
    trackOneClickCheckout("One Click Checkout", cart, "clicked");
    resetState();
    if (customerId && cartId) {
      const oneClickCheckoutResponse = await doOneClickCheckout({
        customerID: customerId,
        cartId: cartId,
        cart: cart,
      });
      console.log("processPayment UI RESPONSE", oneClickCheckoutResponse);

      // SUCCESS
      if (
        oneClickCheckoutResponse?.data &&
        oneClickCheckoutResponse?.data?.status === "success" &&
        oneClickCheckoutResponse?.data?.transaction_type === "purchase"
      ) {
        redirectTimeout = setTimeout(() => {
          setRedirecting(true);
          trackOneClickCheckout("One Click Checkout", cart, "completed");
          const redirectURL = `/vino-checkout/?cartId=${cartId}`;
          window.location.assign(redirectURL);
        }, 1500);
      }

      // ERROR
      if (oneClickCheckoutResponse?.error)
        openToast(
          <ErrorToast message={oneClickCheckoutResponse?.error?.title} />
        );
    }
  };

  useEffect(() => {
    if (user?.addresses?.length > 0) setAddress(user?.addresses[0]);
    if (user?.stored_instruments?.length > 0)
      setDefaultCard(user?.stored_instruments[0]);
  }, [user]);

  useEffect(() => {
    setRedirecting(false);
    return () => {
      resetState();
      clearTimeout(redirectTimeout);
    };
  }, []);

  const isDisabled =
    isBuying || isProcessing || isRedirecting || isLoading || cartRedirect;

  return user &&
    user?.addresses &&
    user?.addresses?.length > 0 &&
    user?.stored_instruments ? (
    <OneClickCheckoutButton
      variant="primary"
      borderColor={isDisabled ? "disabled" : buttonColour}
      backgroundColor={isDisabled ? "disabled" : buttonBorderColor}
      color={isDisabled ? "vader" : buttonTextColor}
      fontSize={isDisabled ? "18px" : "inherit"}
      disabled={isDisabled}
      width="100%"
      my="15px"
      onClick={handleOneClickCheckoutClick}
    >
      {(isBuying && processMessage) || isRedirecting ? (
        <span>{isRedirecting ? "Confirming order!..." : processMessage}</span>
      ) : buttonText ? (
        <span>{buttonText}</span>
      ) : (
        <>
          <span>{"Buy Now - BOOM!"}</span>
          <br />
          <small
            title={`One-click checkout using your ${
              defaultCard?.brand ? defaultCard?.brand + " card" : " card"
            } which card number ending with  ${
              defaultCard?.last_4 ?? "XXXX"
            } and through these address ${address?.address1 ?? null}, ${
              address?.postal_code ?? null
            }`}
          >
            {`${defaultCard?.brand ?? "CARD"} ${defaultCard?.last_4 ?? "XXXX"}`}{" "}
            | {`${address?.address1 ?? null}, ${address?.postal_code ?? null}`}
          </small>
        </>
      )}
    </OneClickCheckoutButton>
  ) : null;
};
